import React, { useRef, useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import emailjs from 'emailjs-com';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaBuilding,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaPaperPlane,
  FaCheckCircle,
  FaLandmark,
  FaBook,
  FaHeartbeat,
  FaShoppingBasket,
  FaClipboardList,
  FaFileInvoice,
  FaMoneyBillWave,
  FaBoxOpen,
  FaFilePdf,
  FaDownload,
  FaLaptop,
  FaWhatsapp,
  FaArrowRight
} from 'react-icons/fa';
import './Tentplacementregistration.css';

const EVENT = {
  name: 'Robofest: Back to Basics 2026',
  shortName: 'Robofest Back to Basics 2026',
  date: 'November 21, 2026',
  venue: 'Woodlands International School',
  motto: 'Inspire. Innovate. Impact.',
  // Place the PDF at this path in your project's public/ folder
  // (e.g. public/downloads/Back-to-Basics-IRC-2026-Tent-Placement-Form.pdf)
  downloadUrl: '/downloads/STEMTRIX_Tent_Placement_Proposal_2026.pdf (1).pdf.pdf'
};

const ZONES = [
  {
    name: 'Lifestyle & Wealth',
    tag: 'High Visibility',
    icon: <FaLandmark />,
    categories: 'Banking, Wealth Management, Insurance, Real Estate, Premium Automotive.',
    objectives: 'Family financial consultations, mortgage leads, and luxury asset showcases.'
  },
  {
    name: 'EdTech & Learning',
    tag: 'Direct Demo',
    icon: <FaBook />,
    categories: 'Publishers, Academic Institutes, Language Schools, Bookstores, Creative Kits.',
    objectives: 'Student enrolments, software sign-ups, and interactive learning demos.'
  },
  {
    name: 'Health & Wellness',
    tag: 'Interactive',
    icon: <FaHeartbeat />,
    categories: 'Family Healthcare, Nutrition, Sports Academies, Wellness & Fitness.',
    objectives: 'Health screenings, sports club registrations, and family wellness advisory.'
  },
  {
    name: 'Gourmet & Retail',
    tag: 'High Traffic',
    icon: <FaShoppingBasket />,
    categories: 'Artisanal Foods, Refreshments, Apparel, Consumer Electronics & Gadgets.',
    objectives: 'Direct product sales, sampling, brand experience, and immediate sales.'
  }
];

const DELIVERABLES = [
  { label: 'Physical Booth Space', detail: 'Dedicated 3m × 3m High-Peak Tent Footprint in prime spectator promenade.' },
  { label: 'Furniture Included', detail: '1 Trestle Display Table, 2 Executive Chairs, and basic setup assistance.' },
  { label: 'Brand Visibility', detail: 'Branded header banner mounting area + inclusion in official Event Vendor Guide.' },
  { label: 'Direct Marketing', detail: 'Permission to distribute promotional flyers, gift items, or samples directly to parents.' },
  { label: 'PA System Mentions', detail: '2 × Live promotional announcements over the event PA system during break intervals.' },
  { label: 'Power Connection', detail: 'Basic power extension access provided upon prior booking (for tablets/displays).' }
];

const STEPS = [
  { icon: <FaClipboardList />, title: 'Submit booking details', text: 'Complete the reservation form below.' },
  { icon: <FaFileInvoice />, title: 'Receive official invoice', text: "You'll get a STEMTRIX invoice by email." },
  { icon: <FaMoneyBillWave />, title: 'Remit payment', text: 'KES 15,000 per tent space.' },
  { icon: <FaBoxOpen />, title: 'Receive vendor pass & kit', text: 'Your pass and logistics kit arrive ahead of the event.' }
];

const TICKET_PRICE = 15000;

const TentPlacementRegistration = () => {
  const form = useRef();
  const [status, setStatus] = useState({ message: '', variant: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [tentCount, setTentCount] = useState(1);
  const [zoneChoice, setZoneChoice] = useState(ZONES[0].name);

  const total = tentCount ? Number(tentCount) * TICKET_PRICE : 0;

  const sendReservation = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({ message: '', variant: '' });

    const formData = new FormData(form.current);
    const companyName = formData.get('company_name') || 'N/A';
    const contactPerson = formData.get('contact_person') || 'N/A';
    const email = formData.get('user_email') || 'N/A';
    const phone = formData.get('phone') || 'N/A';
    const specialRequirements = formData.get('special_requirements') || 'None';

    const summary = [
      `Event: ${EVENT.name}`,
      `Company / Brand Name: ${companyName}`,
      `Contact Person: ${contactPerson}`,
      `Phone Number: ${phone}`,
      `Industry Zone Choice: ${zoneChoice}`,
      `Number of Tents: ${tentCount} (KES ${total.toLocaleString()} total)`,
      `Special Requirements: ${specialRequirements}`
    ].join('\n');

    emailjs
      .send(
        'service_524d26b',
        'template_6pouz1e',
        {
          user_name: contactPerson,
          user_email: email,
          subject: `Tent Placement Booking — ${companyName}`,
          message: summary
        },
        'gVucfB5W8IvVScEUw'
      )
      .then(() => {
        setStatus({
          message: "Reservation request sent! We'll follow up with your invoice shortly.",
          variant: 'success'
        });
        form.current.reset();
        setTentCount(1);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setStatus({
          message: 'Something went wrong sending your reservation. Please try again or contact us directly.',
          variant: 'danger'
        });
        setIsLoading(false);
      });
  };

  return (
    <main className="tpr-page">
      <Helmet>
        <title>Sponsor &amp; Exhibitor Tent Placement — {EVENT.name} | Stemtrix</title>
        <meta
          name="description"
          content={`Reserve a vendor tent at the ${EVENT.name}, ${EVENT.date} at ${EVENT.venue} — the event launching the new Inspire Robotics Challenge 2027 season. Reach families from Nairobi's leading private and international schools.`}
        />
        {/* NOTE: replace with your real production domain */}
        <link rel="canonical" href="https://www.stemtrix.co.ke/sponsors" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Sponsor & Exhibitor Tent Placement — ${EVENT.name}`} />
        <meta
          property="og:description"
          content={`Reserve a vendor tent at ${EVENT.shortName}, ${EVENT.date} at ${EVENT.venue}.`}
        />
        <meta property="og:url" content="https://www.stemtrix.co.ke/sponsors" />
        <meta name="twitter:card" content="summary" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: EVENT.name,
            startDate: "2026-11-21",
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            location: {
              "@type": "Place",
              name: EVENT.venue,
              address: { "@type": "PostalAddress", addressLocality: "Nairobi", addressCountry: "KE" }
            },
            organizer: { "@type": "Organization", name: "Stemtrix", url: "https://www.stemtrix.co.ke/" },
            description:
              "A friendly robotics and coding showcase bringing schools together, and the official launch of the Inspire Robotics Challenge 2027 season. Sponsor and exhibitor tent placements available.",
            offers: {
              "@type": "Offer",
              price: "15000",
              priceCurrency: "KES",
              availability: "https://schema.org/InStock",
              url: "https://www.stemtrix.co.ke/sponsors"
            }
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <header className="tpr-hero">
        <div className="tpr-hero-overlay"></div>
        <Container>
          <div className="tpr-hero-inner">
            <div className="eyebrow justify-content-center">brand partners · vendors · exhibitors</div>
            <h1 className="tpr-hero-title">Tent Placement &amp; Exhibition</h1>
            <p className="tpr-hero-event">
              <span className="line-1">Robofest</span>
              <span className="line-2">Back to Basics 2026</span>
            </p>
            <div className="tpr-hero-meta">
              <span><FaCalendarAlt /> {EVENT.date}</span>
              <span><FaMapMarkerAlt /> {EVENT.venue}</span>
              <span><FaUsers /> 1,000+ Attendees</span>
            </div>
            <p className="tpr-hero-motto">{EVENT.motto}</p>
            <div className="tpr-hero-badge">
              Launching the Inspire Robotics Challenge 2027 Season
            </div>
          </div>
        </Container>
      </header>

      {/* Choose How to Register — the two options */}
      <section className="tpr-choice-section">
        <Container>
          <div className="section-header centered mx-auto text-center mb-5">
            <div className="eyebrow justify-content-center">two ways to reserve</div>
            <h2 className="section-title">Choose How You'd Like to Register</h2>
            <p className="section-subtitle">
              Download and fill out the form by hand, or complete your reservation right here on
              our website — whichever is easier for you.
            </p>
          </div>
          <Row className="g-4 justify-content-center">
            <Col md={6} lg={5}>
              <div className="tpr-choice-card">
                <div className="tpr-choice-icon"><FaFilePdf /></div>
                <span className="tpr-choice-label">Option A</span>
                <h3>Download &amp; Fill Manually</h3>
                <p>
                  Download the official reservation form, fill it out by hand, then send it back
                  to us by WhatsApp or email — we'll follow up with your invoice.
                </p>
                <a
                  href={EVENT.downloadUrl}
                  download
                  className="tpr-choice-btn outline"
                >
                  <FaDownload className="me-2" /> Download the Form (PDF)
                </a>
                <div className="tpr-choice-return">
                  <span>Then send it back via:</span>
                  <div className="tpr-choice-return-links">
                    <a href="https://wa.me/+254718463944" target="_blank" rel="noopener noreferrer">
                      <FaWhatsapp /> WhatsApp
                    </a>
                    <a href="mailto:info.stemtrix@gmail.com">
                      <FaEnvelope /> Email
                    </a>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={6} lg={5}>
              <div className="tpr-choice-card highlighted">
                <div className="tpr-choice-icon"><FaLaptop /></div>
                <span className="tpr-choice-label">Option B</span>
                <h3>Register Online</h3>
                <p>
                  Prefer to skip the paperwork? Fill out the same reservation form digitally,
                  right here on our website — it only takes a couple of minutes.
                </p>
                <a href="#reserve" className="tpr-choice-btn solid">
                  <FaArrowRight className="me-2" /> Fill Out the Form Online
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Executive Summary */}
      <section className="tpr-section">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={5}>
              <div className="eyebrow">executive summary</div>
              <h2>Direct Access to High-Net-Worth Families</h2>
            </Col>
            <Col lg={7}>
              <p className="tpr-lede">
                The {EVENT.name}, organized by STEMTRIX, is a friendly robotics and coding
                showcase that brings schools together to share what their students have been
                learning. Held at {EVENT.venue}, the event also marks the official launch of the
                new Inspire Robotics Challenge 2027 season. Bringing together active
                participants, school directors, and family members, {EVENT.name} offers an
                exclusive, targeted engagement platform for forward-thinking businesses and
                brands.
              </p>
              <div className="tpr-callout">
                Securing a dedicated tent placement grants your brand over <strong>6 hours</strong> of
                direct, uninterrupted interaction with families whose children attend top-tier
                private and international schools across Nairobi — ideal for high-touch customer
                acquisition, brand activation, lead generation, and direct product sales.
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Demographic Profile */}
      <section className="tpr-section alt">
        <Container>
          <div className="section-header centered mx-auto text-center mb-5">
            <div className="eyebrow justify-content-center">who you'll reach</div>
            <h2 className="section-title">Demographic Target Profile</h2>
          </div>
          <Row className="g-4">
            <Col md={6}>
              <div className="tpr-demo-card">
                <h3>High-Net-Worth Households (HNWH)</h3>
                <p>
                  <strong>Parent Profile:</strong> C-Suite executives, business owners, diplomats,
                  expatriates, and high-income professionals whose children attend Nairobi's
                  premier international and private institutions.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="tpr-demo-card">
                <h3>Educators &amp; School Leaders</h3>
                <p>
                  <strong>Delegate Profile:</strong> Over 1,000 active learners (ages 4–18)
                  alongside school directors, heads of technology, STEM coaches, and curriculum
                  decision-makers.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Activation Zones */}
      <section className="tpr-section">
        <Container>
          <div className="section-header centered mx-auto text-center mb-5">
            <div className="eyebrow justify-content-center">choose your zone</div>
            <h2 className="section-title">Classified Industry Activation Zones</h2>
            <p className="section-subtitle">
              Vendor tents are organized into distinct industry zones surrounding the main
              robotics competition arenas, to guarantee continuous foot traffic and optimal
              audience flow.
            </p>
          </div>
          <Row className="g-4">
            {ZONES.map((zone) => (
              <Col md={6} lg={3} key={zone.name}>
                <div className="tpr-zone-card">
                  <div className="tpr-zone-icon">{zone.icon}</div>
                  <span className="tpr-zone-tag">{zone.tag}</span>
                  <h4>{zone.name}</h4>
                  <p className="tpr-zone-label">Target categories</p>
                  <p className="tpr-zone-text">{zone.categories}</p>
                  <p className="tpr-zone-label">Exhibitor objectives</p>
                  <p className="tpr-zone-text">{zone.objectives}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Package Specs */}
      <section className="tpr-section alt">
        <Container>
          <div className="section-header centered mx-auto text-center mb-5">
            <div className="eyebrow justify-content-center">what's included</div>
            <h2 className="section-title">Tent Placement Package Specifications</h2>
            <p className="section-subtitle">
              Every vendor space is a turnkey brand activation footprint, priced at a flat rate of{' '}
              <strong>KES {TICKET_PRICE.toLocaleString()} per space</strong>.
            </p>
          </div>
          <div className="tpr-spec-table-wrap">
            <table className="tpr-spec-table">
              <tbody>
                {DELIVERABLES.map((row) => (
                  <tr key={row.label}>
                    <td className="tpr-spec-label">{row.label}</td>
                    <td>{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* Reservation Form */}
      <section id="reserve" className="tpr-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="section-header centered mx-auto text-center mb-4">
                <div className="eyebrow justify-content-center">reserve your space</div>
                <h2 className="section-title">Exhibitor Reservation &amp; Booking Form</h2>
                <p className="section-subtitle">
                  Complete and submit this form to secure your tent placement. Placements are
                  limited and allocated strictly on a first-come, first-served basis.
                </p>
              </div>

              <div className="tpr-form-card">
                {status.message && (
                  <Alert variant={status.variant} className="tpr-alert">
                    {status.variant === 'success' && <FaCheckCircle className="me-2" />}
                    {status.message}
                  </Alert>
                )}

                <Form ref={form} onSubmit={sendReservation}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaBuilding className="label-icon" /> Company / Brand Name</Form.Label>
                        <Form.Control type="text" name="company_name" placeholder="Enter company or brand name" required />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaUser className="label-icon" /> Contact Person</Form.Label>
                        <Form.Control type="text" name="contact_person" placeholder="Enter contact person's name" required />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaPhone className="label-icon" /> Phone Number</Form.Label>
                        <Form.Control type="tel" name="phone" placeholder="Enter phone number" required />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaEnvelope className="label-icon" /> Email Address</Form.Label>
                        <Form.Control type="email" name="user_email" placeholder="Enter email address" required />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaMapMarkerAlt className="label-icon" /> Industry Zone Choice</Form.Label>
                        <Form.Select value={zoneChoice} onChange={(e) => setZoneChoice(e.target.value)}>
                          {ZONES.map((zone) => (
                            <option key={zone.name} value={zone.name}>
                              {zone.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Tents (KES {TICKET_PRICE.toLocaleString()} ea)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={tentCount}
                          onChange={(e) => setTentCount(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label>Special Requirements (Power Access / Display Space)</Form.Label>
                    <Form.Control as="textarea" name="special_requirements" rows={3} placeholder="Optional" />
                  </Form.Group>

                  <div className="tpr-total-row">
                    <span>Estimated Total</span>
                    <strong>KES {total.toLocaleString()}</strong>
                  </div>

                  <Button type="submit" className="tpr-submit-btn w-100" disabled={isLoading}>
                    {isLoading ? 'Submitting...' : (
                      <>
                        <FaPaperPlane className="me-2" /> Submit Reservation Request
                      </>
                    )}
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Next Steps */}
      <section className="tpr-section alt">
        <Container>
          <div className="section-header centered mx-auto text-center mb-5">
            <div className="eyebrow justify-content-center">next steps</div>
            <h2 className="section-title">Partnering With Stemtrix</h2>
            <p className="section-subtitle">
              Tent placements are limited and allocated strictly on a first-come, first-served basis.
            </p>
          </div>
          <Row className="g-4 mb-5">
            {STEPS.map((step, index) => (
              <Col md={6} lg={3} key={step.title}>
                <div className="tpr-step-card">
                  <span className="tpr-step-number">{index + 1}</span>
                  <div className="tpr-step-icon">{step.icon}</div>
                  <h4>{step.title}</h4>
                  <p>{step.text}</p>
                </div>
              </Col>
            ))}
          </Row>

          <div className="tpr-contact-card">
            <h3>STEMTRIX Marketing Team</h3>
            <p className="tpr-contact-name">Simba Elijah Kiage</p>
            <p className="tpr-contact-role">Digital Marketing &amp; Partnerships Lead</p>
            <div className="tpr-contact-links">
              <a href="tel:+254718463944"><FaPhone /> 0718 463 944</a>
              <a href="tel:+254110394940"><FaPhone /> 0110 394 940</a>
              <a href="mailto:info.stemtrix@gmail.com"><FaEnvelope /> info.stemtrix@gmail.com</a>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
};

export default TentPlacementRegistration;